import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SpinnerComponent } from '../../shared/components/spinner.component';
import { PaginationComponent } from '../../shared/components/pagination.component';
import { LgpdService, LgpdRequest } from '../../core/lgpd.service';
import { NotificationService } from '../../core/notification.service';

@Component({
  standalone: true,
  selector: 'app-lgpd-queue',
  imports: [CommonModule, FormsModule, SpinnerComponent, PaginationComponent],
  template: `
    <div class="bg-white p-6 rounded shadow">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-3xl font-serif">Fila LGPD - Solicitações</h1>
        <div class="text-sm text-muted">
          {{ meta.total || 0 }} solicitações
        </div>
      </div>

      <!-- Filtros por Status -->
      <div class="mb-6 flex gap-2">
        <button (click)="filterByStatus('')" [class.bg-primary]="!statusFilter" [class.text-white]="!statusFilter" class="px-4 py-2 border rounded hover:bg-surface">
          Todas ({{ meta.total || 0 }})
        </button>
        <button (click)="filterByStatus('pendente')" [class.bg-yellow-500]="statusFilter === 'pendente'" [class.text-white]="statusFilter === 'pendente'" class="px-4 py-2 border rounded hover:bg-surface">
          Pendentes
        </button>
        <button (click)="filterByStatus('aprovado')" [class.bg-green-500]="statusFilter === 'aprovado'" [class.text-white]="statusFilter === 'aprovado'" class="px-4 py-2 border rounded hover:bg-surface">
          Aprovadas
        </button>
        <button (click)="filterByStatus('rejeitado')" [class.bg-red-500]="statusFilter === 'rejeitado'" [class.text-white]="statusFilter === 'rejeitado'" class="px-4 py-2 border rounded hover:bg-surface">
          Rejeitadas
        </button>
      </div>

      <!-- Tabela de Solicitações -->
      <div class="overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-surface border-b">
              <th class="text-left p-3 text-sm font-medium">Data</th>
              <th class="text-left p-3 text-sm font-medium">Tipo</th>
              <th class="text-left p-3 text-sm font-medium">Solicitante</th>
              <th class="text-left p-3 text-sm font-medium">Fornecedor</th>
              <th class="text-left p-3 text-sm font-medium">Status</th>
              <th class="text-right p-3 text-sm font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let req of requests" class="border-b hover:bg-surface/50">
              <td class="p-3 text-sm">{{ req.criadoEm | date:'dd/MM/yyyy HH:mm' }}</td>
              <td class="p-3 text-sm">
                <span [class]="getTipoClass(req.tipo)">
                  {{ getTipoLabel(req.tipo) }}
                </span>
              </td>
              <td class="p-3 text-sm">
                <div class="font-medium">{{ req.usuarioNome }}</div>
                <div class="text-xs text-muted">{{ req.usuarioEmail }}</div>
              </td>
              <td class="p-3 text-sm">{{ req.fornecedorNome || '—' }}</td>
              <td class="p-3 text-sm">
                <span [class]="getStatusClass(req.status)">
                  {{ getStatusLabel(req.status) }}
                </span>
              </td>
              <td class="p-3 text-sm text-right">
                <button *ngIf="req.status === 'pendente'" (click)="viewDetails(req)" class="text-xs px-2 py-1 border rounded hover:bg-surface mr-1">
                  Ver Detalhes
                </button>
                <button *ngIf="req.status === 'pendente'" (click)="approve(req)" class="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 mr-1">
                  Aprovar
                </button>
                <button *ngIf="req.status === 'pendente'" (click)="openRejectModal(req)" class="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700">
                  Rejeitar
                </button>
                <span *ngIf="req.status !== 'pendente'" class="text-xs text-muted">
                  {{ req.processadoEm | date:'dd/MM/yyyy' }}
                </span>
              </td>
            </tr>
            <tr *ngIf="requests.length === 0 && !loading">
              <td colspan="6" class="p-8 text-center text-muted">
                <div class="text-4xl mb-2">📋</div>
                <p>Nenhuma solicitação encontrada</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginação -->
      <app-pagination
        *ngIf="meta.total > 0"
        [page]="page"
        [pageSize]="pageSize"
        [total]="meta.total || 0"
        (pageChange)="loadPage($event)"
      ></app-pagination>

      <!-- Loading -->
      <div *ngIf="loading" class="text-center py-8">
        <app-spinner></app-spinner>
      </div>
    </div>

    <!-- Modal de Detalhes -->
    <div *ngIf="selectedRequest as req" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" (click)="closeModal()">
      <div class="bg-white rounded-lg p-6 max-w-2xl w-full mx-4" (click)="$event.stopPropagation()">
        <h2 class="text-2xl font-semibold mb-4">Detalhes da Solicitação</h2>
        <div class="space-y-3 mb-6">
          <div>
            <p class="text-sm text-muted">Tipo:</p>
            <p class="font-medium">{{ getTipoLabel(req.tipo) }}</p>
          </div>
          <div>
            <p class="text-sm text-muted">Solicitante:</p>
            <p class="font-medium">{{ req.usuarioNome }} ({{ req.usuarioEmail }})</p>
          </div>
          <div *ngIf="req.fornecedorNome">
            <p class="text-sm text-muted">Fornecedor:</p>
            <p class="font-medium">{{ req.fornecedorNome }}</p>
          </div>
          <div *ngIf="req.motivo">
            <p class="text-sm text-muted">Motivo:</p>
            <div class="p-3 bg-surface rounded border">{{ req.motivo }}</div>
          </div>
          <div>
            <p class="text-sm text-muted">Data da Solicitação:</p>
            <p>{{ req.criadoEm | date:'dd/MM/yyyy HH:mm' }}</p>
          </div>
        </div>
        <div class="flex justify-end gap-2">
          <button (click)="closeModal()" class="px-4 py-2 border rounded hover:bg-surface">
            Fechar
          </button>
          <button *ngIf="req.status === 'pendente'" (click)="approve(req); closeModal()" class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
            Aprovar
          </button>
          <button *ngIf="req.status === 'pendente'" (click)="closeModal(); openRejectModal(req)" class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
            Rejeitar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Rejeição -->
    <div *ngIf="rejectingRequest" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" (click)="closeRejectModal()">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4" (click)="$event.stopPropagation()">
        <h2 class="text-2xl font-semibold mb-4">Rejeitar Solicitação</h2>
        <p class="text-sm text-muted mb-4">Informe o motivo da rejeição:</p>
        <textarea [(ngModel)]="rejectMotivo" rows="4" class="w-full px-3 py-2 border rounded" placeholder="Motivo da rejeição..."></textarea>
        <div class="flex justify-end gap-2 mt-4">
          <button (click)="closeRejectModal()" class="px-4 py-2 border rounded hover:bg-surface">
            Cancelar
          </button>
          <button (click)="confirmReject()" [disabled]="!rejectMotivo.trim()" class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
            Confirmar Rejeição
          </button>
        </div>
      </div>
    </div>
  `
})
export class LgpdQueueComponent implements OnInit {
  requests: LgpdRequest[] = [];
  page = 1;
  pageSize = 20;
  meta: any = {};
  loading = false;
  statusFilter = '';
  selectedRequest: LgpdRequest | null = null;
  rejectingRequest: LgpdRequest | null = null;
  rejectMotivo = '';

  constructor(
    private lgpdSvc: LgpdService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadRequests();
  }

  loadRequests() {
    this.loading = true;
    this.lgpdSvc.listRequests(this.page, this.pageSize, this.statusFilter).subscribe({
      next: (res) => {
        this.requests = res.data || [];
        this.meta = res.meta || {};
        this.loading = false;
      },
      error: (err) => {
        console.error('[LGPD] Erro ao carregar solicitações', err);
        this.notification.error('Erro ao carregar solicitações');
        this.loading = false;
      }
    });
  }

  loadPage(page: number) {
    this.page = page;
    this.loadRequests();
  }

  filterByStatus(status: string) {
    this.statusFilter = status;
    this.page = 1;
    this.loadRequests();
  }

  viewDetails(req: LgpdRequest) {
    this.selectedRequest = req;
  }

  closeModal() {
    this.selectedRequest = null;
  }

  openRejectModal(req: LgpdRequest) {
    this.rejectingRequest = req;
    this.rejectMotivo = '';
  }

  closeRejectModal() {
    this.rejectingRequest = null;
    this.rejectMotivo = '';
  }

  approve(req: LgpdRequest) {
    if (!confirm(`Aprovar solicitação de ${req.usuarioNome}?\n\nIsso irá processar a solicitação de acordo com o tipo.`)) {
      return;
    }

    this.loading = true;
    // Pode passar adminNotes vazio ou customizável
    this.lgpdSvc.approveRequest(req.id, '').subscribe({
      next: () => {
        this.notification.success('Solicitação aprovada com sucesso');
        this.loadRequests();
      },
      error: (err) => {
        console.error('[LGPD] Erro ao aprovar solicitação', err);
        this.notification.error('Erro ao aprovar solicitação');
        this.loading = false;
      }
    });
  }

  confirmReject() {
    if (!this.rejectingRequest || !this.rejectMotivo.trim()) return;

    this.loading = true;
    this.lgpdSvc.rejectRequest(this.rejectingRequest.id, this.rejectMotivo).subscribe({
      next: () => {
        this.notification.success('Solicitação rejeitada');
        this.closeRejectModal();
        this.loadRequests();
      },
      error: (err) => {
        console.error('[LGPD] Erro ao rejeitar solicitação', err);
        this.notification.error('Erro ao rejeitar solicitação');
        this.loading = false;
      }
    });
  }

  getTipoLabel(tipo: string): string {
    const labels: any = {
      'remocao': 'Remoção de Dados',
      'dados': 'Acesso aos Dados',
      'retificacao': 'Retificação'
    };
    return labels[tipo] || tipo;
  }

  getTipoClass(tipo: string): string {
    const classes: any = {
      'remocao': 'px-2 py-1 bg-red-100 text-red-800 rounded text-xs',
      'dados': 'px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs',
      'retificacao': 'px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs'
    };
    return classes[tipo] || 'px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs';
  }

  getStatusLabel(status: string): string {
    const labels: any = {
      'pendente': 'Pendente',
      'aprovado': 'Aprovado',
      'rejeitado': 'Rejeitado',
      'processado': 'Processado'
    };
    return labels[status] || status;
  }

  getStatusClass(status: string): string {
    const classes: any = {
      'pendente': 'px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium',
      'aprovado': 'px-2 py-1 bg-green-100 text-green-800 rounded text-xs',
      'rejeitado': 'px-2 py-1 bg-red-100 text-red-800 rounded text-xs',
      'processado': 'px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs'
    };
    return classes[status] || 'px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs';
  }
}
